import type { ExternalTableSchema } from '@packages/table-builder';
import { psuAwardBulkRowSelectable } from '@apps/customer/domains/psu-awards/psuAwardBulkSelection';

export const psuAwardsTable: ExternalTableSchema = {
  metadata: {
    title: 'PSU Grants Issued',
    entity: 'PSU Award',
  },

  dataSource: {
    endpoint: '/api/award/api/app/p-sUAward/p-sUAward-list',
    method: 'GET',
    rowIdField: 'id',
    responseMapping: {
      items: 'items',
      totalCount: 'totalCount',
    },
    staticQueryParams: {
      PSUStatus: 0,
      ApprovalStatusIds: [0],
      EmployeeStatuses: [0],
    },
    requestParams: {
      Filter: 'table.searchText',
      Sorting: 'table.sorting',
      SkipCount: 'table.pageOffset',
      MaxResultCount: 'table.pageSize',
      ApprovalStatusIds: 'filters.approvalStatusIdOrDefault',
      PSUAcceptance: 'filters.optionAcceptance',
      CreatedFromDate: 'filters.createdFromDate',
      CreatedToDate: 'filters.createdToDate',
      GrantFromDate: 'filters.fromDate',
      GrantToDate: 'filters.toDate',
      EmployeeStatuses: 'filters.employeeStatusIdOrDefault',
      ApprovedFromDate: 'table.fromApproveDate',
      ApprovedToDate: 'table.toApproveDate',
      PublishFromDate: 'table.fromPublishDate',
      PublishToDate: 'table.toPublishDate',
    },

    filters: [
      {
        name: 'CreatedFromDate',
        placeholder: 'Created Date From',
        type: 'date',
      },
      {
        name: 'CreatedToDate',
        placeholder: 'Created Date To',
        type: 'date',
      },
      {
        name: 'FromDate',
        placeholder: 'Issue Date From',
        type: 'date',
      },
      {
        name: 'ToDate',
        placeholder: 'Issue Date To',
        type: 'date',
      },
      {
        name: 'ApprovalStatusIds',
        placeholder: 'Approval Status',
        type: 'asyncSelect',
        multiple: true,
        dataSource: {
          url: '/api/award/api/app/p-sUAward/p-sUAward-statuses',
          labelField: 'name',
          valueField: 'id',
        },
      },
      {
        name: 'PSUAcceptance',
        placeholder: 'Accept Status',
        type: 'asyncSelect',
        dataSource: {
          url: '/api/award/api/app/p-sUAward/p-sUAcceptances',
          labelField: 'name',
          valueField: 'id',
        },
      },
      {
        name: 'GrantFromDate',
        placeholder: 'Grant Date From',
        type: 'date',
      },
      {
        name: 'GrantToDate',
        placeholder: 'Grant Date To',
        type: 'date',
      },
    ],
  },

  columns: [
    {
      field: 'grantNo',
      header: 'Award #',
      sortable: true,
      width: 150,
      visibility: 'locked',
    },
    {
      field: 'participantName',
      header: 'Participant Details',
      type: 'couple',
      sortable: true,
      couple: { titleField: 'participantName', subtitleField: 'employeeId' },
      width: 200,
      visibility: 'locked',
    },
    {
      field: 'issueDate',
      header: 'Grant Date',
      type: 'date',
      sortable: true,
      width: 180,
      align: 'center',
    },
    {
      field: 'grantName',
      header: 'Grant Name',
      sortable: true,
      width: 300,
      align: 'center',
      visibility: 'picker',
    },
    {
      field: 'planName',
      header: 'Plan Name',
      width: 150,
      visibility: 'picker',
    },
    {
      field: 'status',
      header: 'Approval Status',
      type: 'badge',
      sortable: true,
      width: 180,
      align: 'center',
      uppercase: true,
      badge: {
        statusMap: {
          Approved: 'success',
          Cancelled: 'error',
          Rejected: 'error',
          Pending: 'info',
        },
        defaultVariant: 'info',
      },
    },
    {
      field: 'unitsGranted',
      header: 'Awarded Units',
      type: 'number',
      sortable: true,
      width: 180,
      align: 'center',
      visibility: 'picker',
    },
    {
      field: 'vestedUnits',
      header: 'Vested Units',
      type: 'number',
      width: 150,
      align: 'center',
      visibility: 'picker',
    },
    {
      field: 'releasedUnits',
      header: 'Released Units',
      type: 'number',
      sortable: false,
      width: 180,
      align: 'center',
      visibility: 'picker',
    },
    {
      field: 'cancelledUnits',
      header: 'Canceled Units',
      type: 'number',
      sortable: false,
      width: 180,
      align: 'center',
      visibility: 'picker',
    },
    {
      field: 'outStandingValue',
      header: 'Outstanding Value',
      type: 'currency',
      sortable: true,
      width: 180,
      align: 'center',
      visibility: 'picker',
      format: { decimals: 2 },
    },
    {
      field: 'unvestedUnit',
      header: 'Unvested Units',
      type: 'number',
      width: 180,
      align: 'center',
    },
    {
      field: 'outStandingUnits',
      header: 'Outstanding Units',
      type: 'number',
      sortable: false,
      width: 180,
      align: 'center',
      visibility: 'picker',
    },
    {
      field: 'isAwardPublished',
      header: 'Publish Status',
      type: 'badge',
      sortable: true,
      width: 180,
      align: 'center',
      conditionalValues: { true: 'PUBLISHED', false: 'NOT PUBLISHED' },
      badge: {
        // statusMap uses raw value keys (before conditionalValues transforms)
        statusMap: { true: 'success', false: 'info' },
        defaultVariant: 'info',
      },
    },
    {
      field: 'vestingSchedule',
      header: 'Vesting Schedule',
      sortable: true,
      width: 180,
      align: 'center',
      visibility: 'picker',
    },
    {
      field: 'nextVestingDate',
      header: 'Next Vesting Date',
      type: 'date',
      width: 180,
      align: 'center',
      visibility: 'picker',
    },
    {
      field: 'isAccepted',
      header: 'Accept Status',
      type: 'badge',
      sortable: true,
      width: 180,
      align: 'center',
      visibility: 'picker',
      conditionalValues: { true: 'ACCEPTED', false: 'NEEDS ACCEPTANCE' },
      badge: {
        // statusMap uses raw value keys (before conditionalValues transforms)
        statusMap: { true: 'success', false: 'error' },
        defaultVariant: 'info',
      },
    },
    {
      field: 'acceptanceMethod',
      header: 'Acceptance Method',
      type: 'badge',
      sortable: true,
      width: 180,
      align: 'center',
      conditionalValues: { '1': 'ONLINE', '2': 'OFFLINE' },
      badge: {
        // statusMap uses raw value keys (before conditionalValues transforms)
        statusMap: { '1': 'success', '2': 'error' },
        defaultVariant: 'info',
      },
    },
  ],

  actions: {
    row: [
      { id: 'SUMMARY_PSU_AWARDS', label: 'Summary' },
      { id: 'VIEW_PSU_AWARDS', label: 'View' },
      { id: 'EDIT_PSU_AWARDS', label: 'Edit' },
      { id: 'DELETE_PSU_AWARDS', label: 'Delete', variant: 'danger' },
      { id: 'CANCEL_PSU_AWARDS', label: 'Cancel' },
      { id: 'PUBLISH_PSU_AWARDS', label: 'Publish Award' },
      { id: 'VIEW_COMMENTS_PSU_AWARDS', label: 'View Comments' },
      { id: 'VIEW_ACCEPTANCE_PSU_AWARDS', label: 'View Acceptance' },
    ],
    bulk: [
      { id: 'BULK_PUBLISH_PSU_AWARDS', label: 'Publish Award' },
      { id: 'BULK_DELETE_PSU_AWARDS', label: 'Bulk Delete', variant: 'danger' },
      { id: 'BULK_EXPORT_PSU_AWARDS', label: 'Bulk Export' },
    ],
    toolbar: {
      primary: {
        id: 'ADD_PSU_AWARDS',
        label: 'Grant New PSU',
        icon: 'add_circle_outline',
      },
      secondary: [
        {
          label: 'Downloads',
          icon: 'download',
          type: 'download',
          children: [
            { id: 'DOWNLOAD_TEMPLATE_PSU_AWARDS', label: 'Download Template' },
          ],
        },
        {
          label: 'Import',
          icon: 'upload',
          type: 'upload',
          children: [
            { id: 'IMPORT_PSU_AWARDS', label: 'Import Bulk XLS' },
          ],
        },
      ],
    },
  },

  features: {
    search: { enabled: true },
    pagination: { mode: 'infiniteScroll', defaultSize: 50 },
    selection: { keyField: 'id', selectable: psuAwardBulkRowSelectable },
    columnPreferences: { persistenceKey: 'PSU Admin', resizable: true, reorderable: true },
  },
};
